import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from 'carbon-react/lib/components/box';
import { FilterableSelect, Option, Select } from 'carbon-react/lib/components/select';
import classNames from 'classnames';

import {
  FooterMode,
  WizardScreen
} from '@futrli/ui-components/components/sidebar/wizard/screens/wizard-screen/wizard-screen';
import { WizardFormError } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-error/wizard-form-error';
import { WizardRequiredFieldInfo } from '@futrli/ui-components/components/sidebar/wizard/wizard-required-field-info/wizard-required-field-info';
import { WizardFormValidationErrorSummary } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-validation-error-summary/wizard-form-validation-error-summary';
import { useLocalisedConstantsContext } from '@futrli/localisation/core/localised-constants-context';

import { ChartOfAccountsDisplay } from '../../../../chart-of-accounts/types';
import { AccountCategory, TOTALS_CODE } from '../../../../forecast/prediction-builder/types';
import * as styles from './reclassify.scss';
import { COA_RECLASSIFY_FORM_FIELD_IDS } from './constants';

interface Props {
  onClose: () => void;
  onSave: () => void;
  error?: string;
  validationErrors?: Record<string, { id: string; message: string }>;
  selectedAccount?: ChartOfAccountsDisplay;
  setSelectedAccount: (account: ChartOfAccountsDisplay | null) => void;
  accountOptions: ChartOfAccountsDisplay[];
  categoryToMoveTo: string;
  setCategoryToMoveTo: (category: string) => void;
  categories: string[];
  revalidate: () => void;
  hasInitialAccount?: boolean;
}
export const ReclassifyFormScreen = ({
  onClose,
  onSave,
  error,
  validationErrors,
  selectedAccount,
  setSelectedAccount,
  accountOptions,
  categoryToMoveTo,
  setCategoryToMoveTo,
  categories,
  revalidate,
  hasInitialAccount
}: Props) => {
  const intl = useIntl();

  const { accountCategoryNames } = useLocalisedConstantsContext();

  const handleAutocompleteChangeCarbon = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const accountId = event.target.value;
      const newSelectedAccount = accountOptions.filter(o => o.id === accountId)[0];
      setSelectedAccount(newSelectedAccount);
      if (categoryToMoveTo === newSelectedAccount?.category) {
        setCategoryToMoveTo('');
      }
    },
    [accountOptions, setSelectedAccount, categoryToMoveTo, setCategoryToMoveTo]
  );

  const handleCategoryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setCategoryToMoveTo(event.target.value),
    [setCategoryToMoveTo]
  );

  return (
    <WizardScreen
      footerMode={FooterMode.CREATE}
      onCancel={onClose}
      onCreate={onSave}
      createText={<FormattedMessage id="common.save" defaultMessage="Save" />}
    >
      {error && <WizardFormError message={error} mb={3} />}

      <WizardFormValidationErrorSummary errors={validationErrors} mb={3} />

      <WizardRequiredFieldInfo />

      <Box mb={3}>
        <FilterableSelect
          disabled={hasInitialAccount}
          required
          label={intl.formatMessage({
            id: 'common.account_to_reclassify',
            defaultMessage: 'Account to reclassify'
          })}
          placeholder={intl.formatMessage({
            id: 'predict.select_one',
            defaultMessage: 'Select one...'
          })}
          value={selectedAccount?.id || ''}
          onChange={handleAutocompleteChangeCarbon}
          mb={0}
          name={COA_RECLASSIFY_FORM_FIELD_IDS.SELECTED_ACCOUNT}
          id={COA_RECLASSIFY_FORM_FIELD_IDS.SELECTED_ACCOUNT}
          onBlur={revalidate}
        >
          {accountOptions.map((o: ChartOfAccountsDisplay, i: number) => (
            <Option
              key={`${o.id}-${i}`}
              value={o.id}
              text={o.name}
              disabled={o.id === TOTALS_CODE}
              className={classNames({
                [styles.categoryItemCarbon]: o.code === TOTALS_CODE,
                [styles.accountItemCarbon]: o.code !== TOTALS_CODE
              })}
            >
              <div style={{ width: 6 }}></div>
              <div>{o.name}</div>
            </Option>
          ))}
        </FilterableSelect>
      </Box>

      <Box mb={3}>
        <Select
          required
          name={COA_RECLASSIFY_FORM_FIELD_IDS.CATEGORY}
          id={COA_RECLASSIFY_FORM_FIELD_IDS.CATEGORY}
          mb={0}
          value={categoryToMoveTo}
          autoComplete="off"
          label={intl.formatMessage({
            id: 'predict.category_to_move_to',
            defaultMessage: 'Category to move to'
          })}
          onChange={handleCategoryChange}
          placeholder={intl.formatMessage({
            id: 'predict.select_one',
            defaultMessage: 'Select one...'
          })}
          onBlur={revalidate}
        >
          {categories
            .filter(c => selectedAccount?.category !== c && c !== 'equity')
            .map(c => (
              <Option key={c} value={c} text={accountCategoryNames[c as AccountCategory]} />
            ))}
        </Select>
      </Box>
    </WizardScreen>
  );
};
